/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { PhaseStats, Nation, Phase } from "@/types/api.types";

interface AdvancePhaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stats: PhaseStats;
  activeNations: Nation[];
  onConfirm: (newPhase: Phase, eliminatedNationIds: number[]) => void;
  isAdvancing: boolean;
}

const formatPhase = (phase: string) =>
  phase.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function AdvancePhaseDialog({
  open,
  onOpenChange,
  stats,
  activeNations,
  onConfirm,
  isAdvancing,
}: AdvancePhaseDialogProps) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");

  // Reset selection and search every time the dialog opens
  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setSearch("");
    }
  }, [open]);

  const filtered = activeNations.filter((n) =>
    n.name.toLowerCase().includes(search.toLowerCase()) ||
    n.code.toLowerCase().includes(search.toLowerCase())
  );

  const toggleNation = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === activeNations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(activeNations.map((n) => n.id));
    }
  };

  const handleConfirm = () => {
    if (!stats.nextPhase) return;
    onConfirm(stats.nextPhase, selectedIds);
  };

  const totalPlayersEliminated = activeNations
    .filter((n) => selectedIds.includes(n.id))
    .reduce((sum, n) => sum + (n._count?.players ?? 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Advance to {stats.nextPhase ? formatPhase(stats.nextPhase) : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Phase transition info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 font-medium">
              {formatPhase(stats.currentPhase)} →{" "}
              {stats.nextPhase ? formatPhase(stats.nextPhase) : ""}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Select nations to eliminate. Their players will be deactivated and
              removed from fantasy consideration.
            </p>
          </div>

          {/* Select nations to eliminate */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">
                Nations to eliminate ({selectedIds.length} of {activeNations.length} selected)
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={toggleAll}
                className="text-xs text-blue-600"
              >
                {selectedIds.length === activeNations.length ? "Deselect All" : "Select All"}
              </Button>
            </div>

            {/* Search */}
            <Input
              placeholder="Search nation..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-2 h-8 text-sm"
            />

            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="col-span-2 text-center text-gray-400 text-sm py-4">No nations match</p>
              ) : (
                filtered.map((nation) => {
                  const isSelected = selectedIds.includes(nation.id);
                  return (
                    <div
                      key={nation.id}
                      onClick={() => toggleNation(nation.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                        isSelected
                          ? "bg-red-50 border-red-300"
                          : "bg-white border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleNation(nation.id)}
                        className="accent-red-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {nation.flagUrl && (
                        <img
                          src={nation.flagUrl}
                          className={`w-8 h-5 object-cover rounded border ${isSelected ? "grayscale" : ""}`}
                          alt=""
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? "text-red-700 line-through" : "text-gray-800"}`}>
                          {nation.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {nation.code}
                          {nation._count?.players != null && (
                            <span className="ml-1 text-gray-300">· {nation._count.players} players</span>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Summary */}
          {selectedIds.length > 0 ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
              <p className="text-sm text-red-800 font-medium">
                ⚠️ {selectedIds.length} nation(s) will be eliminated
              </p>
              {totalPlayersEliminated > 0 && (
                <p className="text-xs text-red-600">
                  {totalPlayersEliminated} players will be deactivated and removed from fantasy teams
                </p>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-500">
                No nations selected — advancing without eliminating anyone. Make sure this is intentional.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isAdvancing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isAdvancing || !stats.nextPhase}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isAdvancing
              ? "Advancing..."
              : `Advance to ${stats.nextPhase ? formatPhase(stats.nextPhase) : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
